import dayjs, { ConfigType } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import durationPlugin from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isToday from 'dayjs/plugin/isToday';
import minMax from 'dayjs/plugin/minMax';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { formatDate } from '@frontend/date';
import { isValidDate } from '@frontend/schedule-helpers';
import { DATETIME_FORMATS } from '../constants';

// Extend dayjs with the necessary plugins
dayjs.extend(durationPlugin);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isToday);
dayjs.extend(isSameOrAfter);
dayjs.extend(minMax);

export const getLocalTimezone = () => dayjs.tz.guess();

export function sortByTimezone<T>(arr: T[], fieldName: keyof T): T[] {
  return arr.sort((a, b) => {
    try {
      const valA = dayjs().tz(a[fieldName] as string);
      const valB = dayjs().tz(b[fieldName] as string);
      if (!valA.isValid() || !valB.isValid()) {
        throw new Error('Invalid timezone received', { cause: { a, b, fieldName } });
      }
      // Compare the UTC offsets of the two timezones
      const offsetA = valA.utcOffset();
      const offsetB = valB.utcOffset();
      return offsetA - offsetB; // Ascending order based on UTC offset
    } catch (error) {
      console.error(`Error sorting by timezone: ${error}`);
      return 0; // If there's an error, maintain the original order
    }
  });
}

export const TIME_DISPLAY_FORMAT = 'hh:mm A';
export const DATE_DISPLAY_FORMAT = 'MM/DD/YYYY';

export const getTimeString = (date: ConfigType, isToUTC?: boolean): string =>
  formatDate(date, TIME_DISPLAY_FORMAT, isToUTC);

export const getEndTimeString = (startDate: ConfigType, durationInMinutes?: number): string => {
  if (!durationInMinutes) return '';

  const startDateString = formatDate(startDate);
  if (!startDateString) return '';

  return getTimeString(dayjs(startDateString).add(durationInMinutes, 'm'));
};

interface TimeDiffOptions {
  format?: 'HH:mm:ss' | 'hh:mm A' | 'HH:mm'; // The format of the time strings (default: 'HH:mm:ss')
  unit?: dayjs.QUnitType; // The unit for the difference (default: 'm' for minutes)
  crossDay?: boolean; // Whether to consider cross-day time differences (default: false)
}

export const getTimeDiff = (startTime: string, endTime: string, options: TimeDiffOptions = {}): number => {
  const { format = 'HH:mm:ss', unit = 'm', crossDay = false } = options;
  const start = dayjs(startTime, format, true);
  const end = dayjs(endTime, format, true);

  if (!start.isValid() || !end.isValid()) {
    // TODO: add sentry error logging
    console.error('Invalid date format provided for start or end time.');
    return 0;
  }

  let duration = end.diff(start, unit);

  if (crossDay && duration <= 0) {
    // If end time is before start time, assume end time is on the next day
    duration = end.add(1, 'day').diff(start, unit);
  }
  return duration || 0;
};

export const getDateByTimezone = (
  dateTime: dayjs.ConfigType,
  fromFormat: string,
  fromTimezone?: string
): dayjs.Dayjs => {
  try {
    if (fromTimezone) {
      return dayjs.tz(dateTime, fromFormat, fromTimezone);
    }
    return dayjs(dateTime, fromFormat);
  } catch (error) {
    console.error('Error parsing date', error);
    return dayjs(null);
  }
};

export const getDateString = (date: ConfigType): string => formatDate(date, DATE_DISPLAY_FORMAT);

export const getTodaysDate = (): string => dayjs().format(DATE_DISPLAY_FORMAT);

export const isSelectedDateIsToday = (selectedDate: ConfigType): boolean => {
  return dayjs(selectedDate).isSame(dayjs(), 'day');
};
export const getSameDateWithDifferentTimezone = (
  date: ConfigType,
  fromFormat: string,
  toTimezone: string
): dayjs.Dayjs => dayjs(date, fromFormat).tz(toTimezone, true);

export const addDaysToDate = (date: ConfigType, noOfDays: number): string => {
  return dayjs(date).add(noOfDays, 'day').format(DATE_DISPLAY_FORMAT);
};

export const getCalendarStartAndEndDateTime = (
  selectedCalendarDate: string,
  timezone: string,
  isOnboardingFlow = false
) => {
  if (!selectedCalendarDate) {
    return { startDateTime: '', endDateTime: '' };
  }

  const baseDate = getDateByTimezone(selectedCalendarDate, DATE_DISPLAY_FORMAT, timezone);

  let startDate: dayjs.Dayjs;
  let endDate: dayjs.Dayjs;

  if (isOnboardingFlow) {
    startDate = baseDate.subtract(6, 'month').startOf('day');
    endDate = baseDate.add(6, 'month').endOf('day');
  } else {
    startDate = baseDate.startOf('day');
    endDate = baseDate.endOf('day');
  }

  return {
    startDateTime: startDate.utc().tz(timezone).format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
    endDateTime: endDate.utc().tz(timezone).format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
  };
};

export const roundOffMinutes = (time: string, format: string, interval = 5): string => {
  const date = dayjs(time, format);
  if (!isValidDate(date)) {
    return '';
  }

  const roundedTime = date.minute(Math.round(date.minute() / interval) * interval).second(0);

  return dayjs(roundedTime).format(format);
};

export const isSameOrAfterDate = (date1: dayjs.Dayjs, date2: dayjs.Dayjs): boolean => {
  return date1.isSameOrAfter(date2);
};

export const isTodayDate = (date: dayjs.Dayjs): boolean => {
  return date.isToday();
};

export const getMinDate = (dates: dayjs.Dayjs[]): dayjs.Dayjs | null => {
  if (dates.length === 0) {
    return null;
  }
  return dayjs.min(dates);
};

/**
 * Returns true if the user's browser timezone is different from any location's timezone.
 * Checking Timezone offset as Timezone string can be different for same timezone.
 */
export const isUserTimezoneDifferent = (locationTimezones: string[]): boolean => {
  const now = dayjs();
  const userTimezone = dayjs.tz.guess();
  const userOffset = now.tz(userTimezone).utcOffset();

  return locationTimezones.some((timezone) => {
    if (!timezone) return false;
    return now.tz(timezone).utcOffset() !== userOffset;
  });
};

// Returns timezone details of a given IANA name (e.g. 'America/Los_Angeles')
export function getTimezoneDetails(timezone: string) {
  try {
    const tzDate = dayjs().tz(timezone);
    return {
      name: timezone,
      abbr: tzDate.format('z'), // PDT, PST
      utcOffset: tzDate.utcOffset(),
      todayDate: tzDate.format(DATE_DISPLAY_FORMAT),
    };
  } catch (error) {
    console.error(`Invalid timezone: ${timezone}. Error: ${error}`);
    return {
      name: '',
      abbr: '',
      utcOffset: '',
      todayDate: '',
    };
  }
}

// Compare the UTC offset of two IANA timezones at the current time and returns true if the offsets are different.
export function isUserTimezoneOffsetDifferent(userTimezone: string, compareTimezone: string): boolean {
  const now = dayjs();
  try {
    return now.tz(userTimezone).utcOffset() !== now.tz(compareTimezone).utcOffset();
  } catch (error) {
    console.error('Error comparing timezones', error);
    return true;
  }
}

export const getWeekRange = (weekInView: string, timeZone?: string) => ({
  startOfWeek: startOfWeek(weekInView, timeZone),
  endOfWeek: endOfWeek(weekInView, timeZone),
});

export const getWeekRangeText = (weekInView: string, timeZone?: string) => {
  const { startOfWeek, endOfWeek } = getWeekRange(weekInView, timeZone);

  const isSameYear = startOfWeek.year() === endOfWeek.year();

  if (isSameYear) {
    return `${startOfWeek.format('MMM D')} - ${endOfWeek.format('MMM D')}, ${startOfWeek.year()}`;
  }

  return `${startOfWeek.format('MMM D, YYYY')} - ${endOfWeek.format('MMM D, YYYY')}`;
};

// Returns the start of the week (Sunday) for a given date
export const startOfWeek = (date: ConfigType, timeZone?: string) => {
  const dateObj = timeZone ? dayjs.tz(date, timeZone) : dayjs(date);
  return dateObj.day(0).startOf('day');
};

// Returns the end of the week (Saturday) for a given date
export const endOfWeek = (date: ConfigType, timeZone?: string) => {
  const dateObj = timeZone ? dayjs.tz(date, timeZone) : dayjs(date);
  return dateObj.day(6).endOf('day');
};
